// Browser side of the profile photo: decoding the picked file, drawing the
// crop editor and encoding the chosen square through a canvas (no EXIF; the
// server re-encodes it again), then `PUT /me/avatar` with progress.
import type { Schemas } from "@10twenty/api";
import type { SessionManager } from "../auth/session";
import { sendBlob } from "../blobUpload";
import { downscaleSteps, encodeTypes, IMAGE_QUALITY } from "../chat/media";
import { AVATAR_FILE_MAX_BYTES, AVATAR_MIN_PX, cropRect, outputSide, tooSmall, type Crop, type Size } from "./avatar";

/** Why a picked photo can't be used, in words for the card. */
export class PhotoError extends Error {}

export interface Photo {
  bitmap: ImageBitmap;
  size: Size;
  /** The file's type, which picks the fallback encoding (PNG keeps transparency). */
  type: string;
}

/** Decodes a picked or dropped file (orientation applied). */
export async function loadPhoto(file: File): Promise<Photo> {
  if (file.type && !file.type.startsWith("image/")) throw new PhotoError("Choose a photo.");
  if (file.size > AVATAR_FILE_MAX_BYTES) throw new PhotoError("This photo is too large.");
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new PhotoError("This browser can't read this photo's format.");
  }
  const size = { width: bitmap.width, height: bitmap.height };
  if (tooSmall(size)) {
    bitmap.close();
    throw new PhotoError(`This photo is too small. Choose one at least ${AVATAR_MIN_PX} pixels across.`);
  }
  return { bitmap, size, type: file.type };
}

/** Draws the crop into the editor's canvas, sized for the screen's pixel ratio. */
export function drawPreview(canvas: HTMLCanvasElement, photo: Photo, crop: Crop): void {
  const cssPx = canvas.clientWidth;
  if (cssPx <= 0) return;
  const px = Math.round(cssPx * (window.devicePixelRatio || 1));
  if (canvas.width !== px || canvas.height !== px) {
    canvas.width = px;
    canvas.height = px;
  }
  const context = canvas.getContext("2d");
  if (!context) return;
  const { sx, sy, side } = cropRect(photo.size, crop);
  context.imageSmoothingQuality = "high";
  context.clearRect(0, 0, px, px);
  context.drawImage(photo.bitmap, sx, sy, side, side, 0, 0, px, px);
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** The chosen square at most 1,024 px, as WebP (else PNG/JPEG), halving on the way so it doesn't alias. */
export async function encodeCrop(photo: Photo, crop: Crop): Promise<Blob> {
  const { sx, sy, side } = cropRect(photo.size, crop);
  const out = outputSide(photo.size, crop);
  let source: CanvasImageSource = photo.bitmap;
  let rect = [sx, sy, side, side] as const;
  let canvas: HTMLCanvasElement | null = null;
  for (const step of downscaleSteps({ width: side, height: side }, { width: out, height: out })) {
    canvas = document.createElement("canvas");
    canvas.width = step.width;
    canvas.height = step.height;
    const context = canvas.getContext("2d");
    if (!context) throw new PhotoError("This browser can't prepare photos.");
    context.imageSmoothingQuality = "high";
    context.drawImage(source, ...rect, 0, 0, step.width, step.height);
    source = canvas;
    rect = [0, 0, step.width, step.height];
  }
  if (!canvas) throw new PhotoError("This browser can't prepare photos.");
  for (const type of encodeTypes(photo.type)) {
    const blob = await toBlob(canvas, type, IMAGE_QUALITY);
    // A browser that can't encode a type falls back to PNG; try the next.
    if (blob && blob.type === type) return blob;
  }
  throw new PhotoError("This browser can't prepare photos.");
}

/** `PUT /me/avatar`: the user with the new photo's URLs. */
export function uploadAvatar(
  session: SessionManager,
  blob: Blob,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<Schemas["User"]> {
  return sendBlob<Schemas["User"]>(session, {
    method: "PUT",
    path: "/me/avatar",
    query: {},
    body: blob,
    contentType: blob.type || "application/octet-stream",
    onProgress,
    signal,
  });
}
